import { httpResource } from '@angular/common/http';
import { computed, effect, inject, Injectable, signal, untracked } from '@angular/core';

import { AuthStore } from '@rpgc-ai/store/auth/auth.store';
import { ChatsStore } from '@rpgc-ai/store/chats/chats.store';
import { ProfileStore } from '@rpgc-ai/store/profile/profile.store';

import type { IChatsResponse } from '@rpgc-ai/shared-interfaces/interfaces/chats.interface';

import { filter, isEmpty, map, toPairs } from 'lodash-es';

import { AbstractCache } from '../utils/abstract-cache';

@Injectable({
  providedIn: 'root',
})
export class ChatsService extends AbstractCache<IChatsResponse> {
  readonly #chatsStore = inject(ChatsStore);
  readonly #authStore = inject(AuthStore);
  readonly #profileStore = inject(ProfileStore);

  readonly loadChats = signal<boolean>(false);
  /**
   * Ресурс для загрузки чатов
   */
  readonly #chatsResource = httpResource<IChatsResponse>(() => {
    const uri = this.uri();
    const session = this.#authStore.session();
    const { id } = this.#profileStore.profile() || {};

    //TODO hidden until cache ref
    // Если данные есть в кеше
    // if (this.lastRequest === uri || this.isCachedData(uri)) {
    //   return;
    // }

    if (!session || !id) {
      return;
    }
    const params: Record<string, string | number | boolean> = {
      includeFullCharacter: true,
      includeLastUsed: false,
    };

    return {
      url: uri,
      method: 'GET',
      params,
    };
  });

  readonly isLoading = this.#chatsResource.isLoading;

  /**
   * Uri запроса с фильтрами
   */
  readonly uri = computed<string>(() => {
    const filters = this.#chatsStore['sanitizedFilters']();
    const params = map(
      // Специальная обработка для разных типов значений
      filter(toPairs(filters), ([key, value]) => {
        // Если это null, удаляем из фильтров
        if (value === null) {
          return false;
        }

        // Если это числовое значение (включая 0), сохраняем его
        if (typeof value === 'number') {
          return true;
        }

        // Если это boolean, сохраняем его
        if (typeof value === 'boolean') {
          return true;
        }

        // Для всех остальных полей проверяем на пустоту
        return !isEmpty(value);
      }),
      ([key, value]) => `${key}=${value}`,
    );

    return `chats?${params.join('&')}`;
  });

  constructor() {
    super();
    this.#loadChatsEffect();
    this.#changeFiltersEffect();
    this.#checkCacheEffect();
  }

  reloadChats(options?: { ignoreCache: boolean }): void {
    //TODO hidden until cache ref

    // const uri = this.uri();
    // const cachedData = this.requestCache.get(uri);
    // const { ignoreCache } = options ?? {};

    // if (cachedData && !ignoreCache) {
    //   this.#processChatsData(cachedData);
    // } else {
    this.#chatsResource.reload();
    // }
  }

  /**
   * Эффект изменения фильтров
   */
  #changeFiltersEffect(): void {
    effect(() => {
      const filters = this.#chatsStore['sanitizedFilters']();
      untracked(() => {
        this.#chatsStore.setLoading(true);
        // Автоматически загружаем персонажей при изменении фильтров
        this.#chatsResource.reload();
        return filters;
      });
    });
  }

  /**
   * Эффект загрузки чатов
   */
  #loadChatsEffect(): void {
    effect(() => {
      const isError = this.#chatsResource.error();
      const isLoading = this.#chatsResource.isLoading();
      const loadChats = this.loadChats();

      untracked(() => {
        this.#chatsStore.setLoading(false);

        if (loadChats !== true) {
          return;
        }

        // Обрабатываем только когда загрузка завершена и нет ошибок
        if (!isLoading && !isError) {
          try {
            const value = this.#chatsResource.value();
            if (value) {
              // Сохраняем URI последнего успешного запроса
              this.lastRequest = this.uri();

              // Сохраняем данные в кеш
              if (this.lastRequest) {
                this.requestCache.set(this.lastRequest, value);
              }

              this.#processChatsData(value);
            }
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error(error);
          }
        }
      });
    });
  }

  /**
   * Эффект при изменении фильтра если есть в кеше
   */
  #checkCacheEffect(): void {
    effect(() => {
      const uri = this.uri();
      const cachedData = this.requestCache.get(uri);

      untracked(() => {
        this.#chatsStore.setLoading(false);
        if (cachedData) {
          this.#processChatsData(cachedData);
        }
      });
    });
  }

  #processChatsData(value: IChatsResponse): void {
    const { data, meta } = value;
    if (!this.lastRequest.includes('search')) {
      this.#chatsStore.setChatsCount(data.length);
    }
    this.#chatsStore.setChats(data);
    this.#chatsStore.setChatsMeta(meta);
  }
}
